import React, { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDButton from "components/MDButton";
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import { Edit, Save } from "@mui/icons-material";
import { useEditAcademicYear } from "hooks/settingsHooks/useEditAcademicYear";
import { API_URL } from "../../../../config";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { toast } from "sonner";

const EditAcademicYearPopup = ({ academicyear }) => {
  const [open, setOpen] = useState(false);
  const { user } = useAuthContext();
  const [academicyearname, setAcademicYearName] = useState("");
  const { editacademicyear, error, isLoading, success, setError, setSuccess } =
    useEditAcademicYear();
  const [isAcademicYear, setIsAcademicYear] = useState(false);

  // Set initial state and update on prop changes
  useEffect(() => {
    if (academicyear) {
      setAcademicYearName(academicyear.year || "");
      setIsAcademicYear(academicyear.isCurrentAcademicYear || false);
    }
  }, [academicyear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await editacademicyear(academicyear.id, academicyearname);
    handleClose();
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setError(null);
    setSuccess(null);
  });

  // Handle success and error messages
  useEffect(() => {
    if (error) {
      toast.error("Failed to edit academic year", {
        description: error.message,
      });
    }
    if (success) {
      toast.success("Academic year edited successfully");
      handleClose();
    }
  }, [error, handleClose, success]);

  const handleToggleChange = async (event) => {
    const isChecked = event.target.checked;
    setIsAcademicYear(isChecked);

    try {
      const response = await fetch(
        `${API_URL}/academicyears/setcurrentacademicyear/${academicyear.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ setStatus: isChecked }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData.message || "Error updating academic year";

        toast.error("Failed to update academic year", {
          description: errorMessage,
        });

        // Revert the toggle state on error
        setIsAcademicYear(!isChecked);
      } else {
        toast.success("Success", {
          description: isChecked
            ? "Academic Year set as active successfully"
            : "Academic Year removed from active successfully",
        });
      }
    } catch (error) {
      console.error("Error updating academic year:", error);
      toast.error("Error updating academic year", {
        description: error.message,
      });

      // Revert the toggle state on error
      setIsAcademicYear(!isChecked);
    }
  };

  return (
    <div>
      <Button onClick={handleClickOpen}>
        <Edit sx={{ cursor: "pointer", fontWeight: "bold" }} color="warning" />
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { padding: 20, borderRadius: 10 },
        }}
      >
        <DialogTitle>Edit Academic Year</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center">
            <TextField
              label="Academic Year..."
              name="academicYear"
              value={academicyearname}
              onChange={(e) => setAcademicYearName(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
            />
          </Box>
        </DialogContent>
        <Box display="flex" justifyContent="center" alignItems="center">
          <DialogContent>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isAcademicYear}
                    onChange={handleToggleChange}
                    color="primary"
                  />
                }
                label={
                  isAcademicYear
                    ? "Remove from current academic year"
                    : "Set as current academic year"
                }
              />
            </div>
          </DialogContent>
        </Box>
        <DialogActions>
          <MDButton onClick={handleClose}>Cancel</MDButton>
          <MDButton
            onClick={handleSubmit}
            variant="contained"
            color="info"
            disabled={isLoading}
            startIcon={<Save />}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditAcademicYearPopup;
